<template>
  <div>
    <Head title="Editar producto" />
    <div class="flex items-center gap-4 mb-8">
      <button class="btn-secondary" @click="back">
        <i class="fa fa-arrow-left mr-2"></i>
        Volver
      </button>
      <h1 class="text-3xl font-bold flex items-center gap-2">
        <Link class="text-indigo-400 hover:text-indigo-600" href="/products">Productos</Link>
        <span class="text-indigo-400 font-medium">/</span>
        <Link class="text-indigo-400 hover:text-indigo-600" :href="`/products/${product.id}`">{{ form.name }}</Link>
        <span class="text-indigo-400 font-medium">/</span>
        <span>Editar</span>
      </h1>
    </div>

    <div class="max-w-3xl bg-white rounded-xl shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="p-8">
          <!-- Basic Information -->
          <div class="mb-8">
            <h2 class="text-lg font-semibold mb-4">Información Básica</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <text-input
                v-model="form.code"
                :error="form.errors.code"
                label="Código"
                required
              />
              <text-input
                v-model="form.name"
                :error="form.errors.name"
                label="Nombre"
                required
              />
              <text-input
                v-model="form.description"
                :error="form.errors.description"
                label="Descripción"
                type="textarea"
                class="lg:col-span-2"
              />
            </div>
          </div>

          <!-- Pricing & Cost -->
          <div class="mb-8">
            <h2 class="text-lg font-semibold mb-4">Precios y Costos</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <text-input
                v-for="(group, key) in groups"
                :key="group.id"
                v-model.number="form.prices[key]"
                type="numeric"
                :error="form.errors.price"
                :label="`Precio ${group.name}`"
              />
              <text-input
                v-model="form.cost"
                :error="form.errors.cost"
                label="Costo"
                type="number"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <!-- Classification -->
          <div class="mb-8">
            <h2 class="text-lg font-semibold mb-4">Clasificación</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <select-input
                v-model="form.brand_id"
                :error="form.errors.brand_id"
                label="Marca"
                required
              >
                <option value="">Seleccionar</option>
                <option
                  v-for="brand in brands"
                  :key="brand.id"
                  :value="brand.id"
                >
                  {{ brand.name }}
                </option>
              </select-input>

              <select-input
                v-model="form.product_category_id"
                :error="form.errors.product_category_id"
                label="Categoría"
                required
              >
                <option value="">Seleccionar</option>
                <option
                  v-for="category in productCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select-input>

              <select-input
                v-model="form.contable"
                :error="form.errors.contable"
                label="Contable"
              >
                <option value="">Seleccionar</option>
                <option :value="0">No</option>
                <option :value="1">Si</option>
              </select-input>

              <text-input
                v-model="form.location"
                :error="form.errors.location"
                label="Ubicación"
              />
            </div>
          </div>

          <div class="mb-8">
            <h2 class="text-lg font-semibold mb-4">Campos Adicionales</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <text-input
                v-model="form.min_quantity"
                :error="form.errors.min_quantity"
                label="Cantidad Mínima"
                type="number"
                min="0"
                step="1"
              />
              <text-input
                v-model="form.sequence"
                :error="form.errors.sequence"
                label="Secuencia"
                type="number"
                min="0"
                step="1"
              />
              <text-input
                v-model="form.threshold"
                :error="form.errors.threshold"
                label="Umbral"
                type="number"
                min="0"
                step="1"
              />
            </div>
          </div>

          <!-- Images -->
          <!-- Inside the Images section of the template -->
          <div class="mb-8">
            <h2 class="text-lg font-semibold mb-4">Imágenes del Producto</h2>
            <div class="space-y-4">
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <!-- Existing Photos -->
                <div
                  v-for="photo in existingPhotos"
                  :key="photo.id"
                  class="relative group"
                >
                  <img
                    :src="`/storage/${photo.photo}`"
                    class="w-full h-32 object-cover rounded-lg"
                    alt="Product Photo"
                  />
                  <button
                    @click.prevent="removeExistingPhoto(photo.id)"
                    class="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>

                <!-- New Photo Previews -->
                <div
                  v-for="(preview, index) in imagePreviewUrls"
                  :key="'new-' + index"
                  class="relative group"
                >
                  <img
                    :src="preview"
                    class="w-full h-32 object-cover rounded-lg"
                    alt="Preview"
                  />
                  <button
                    @click.prevent="removeImage(index)"
                    class="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>

              <div class="flex items-center justify-center w-full">
                <label
                  class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div class="flex flex-col items-center justify-center pt-5 pb-6">
                    <i class="fa fa-cloud-upload-alt text-2xl text-gray-500 mb-2"></i>
                    <p class="text-sm text-gray-500">Haga clic para cargar o arrastre las imágenes aquí</p>
                    <p class="text-xs text-gray-500">(Máximo 5 imágenes)</p>
                  </div>
                  <input
                    type="file"
                    class="hidden"
                    accept="image/*"
                    multiple
                    @change="handleImageUpload"
                    ref="fileInput"
                  />
                </label>
              </div>
              <p v-if="form.errors.photos" class="text-red-500 text-sm">{{ form.errors.photos }}</p>
            </div>
          </div>

          

          <span v-html="barcode"></span>
        </div>

        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Eliminar producto</button>
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            <i class="fa fa-save mr-2"></i>
            Actualizar producto
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import FileInput from '@/Shared/FileInput'

export default {
  name: 'EditProduct',
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    FileInput
  },
  layout: Layout,
  remember: 'form',
  props: {
    product: Object,
    groups: Object,
    brands: Object,
    parts: Object,
    productCategories: Object,
    barcode: Object
  },
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        code: this.product.code,
        id: this.product.id,
        name: this.product.name,
        line_name: this.product.line_name,
        description: this.product.description,
        volumen: this.product.volumen,
        weight: this.product.weight,
        prices: this.product?.prices?.map(price => price.price) || [],
        brand_id: this.product.brand_id,
        part_id: this.product.part_id,
        alias: this.product.alias,
        contable: this.product.contable,
        cost: this.product.cost,
        photos: [],
        deleted_photos: [],
        product_category_id: this.product.product_category_id,
        location: this.product.location,
        min_quantity: this.product.min_quantity,
        sequence: this.product.sequence,
        threshold: this.product.threshold,
      }),
      imagePreviewUrls: [],
      existingPhotos: this.product.photos || []
    }
  },
  methods: {
    back() {
      window.history.back()
    },
    handleImageUpload(event) {
      const files = Array.from(event.target.files)
      if (files.length > 5) {
        alert('Solo se permiten hasta 5 imágenes')
        return
      }

      files.forEach(file => {
        if (!file.type.startsWith('image/')) {
          alert('Por favor, seleccione solo archivos de imagen')
          return
        }

        this.form.photos.push(file)
        const reader = new FileReader()
        reader.onload = e => {
          this.imagePreviewUrls.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
    },
    removeImage(index) {
      this.form.photos.splice(index, 1)
      this.imagePreviewUrls.splice(index, 1)
    },
    removeExistingPhoto(photoId) {
      this.$inertia.delete('/products/photos/' + photoId + '/delete');
    },
    update() {
      if (!this.form.code || !this.form.name || !this.form.brand_id || !this.form.product_category_id) {
        alert('Por favor, complete todos los campos requeridos')
        return
      }

      const formData = new FormData()
      Object.keys(this.form).forEach(key => {
        if (key === 'photos') {
          this.form.photos.forEach(photo => {
            formData.append('photos[]', photo)
          })
        } else if (key === 'prices') {
          this.form.prices.forEach((price, index) => {
            formData.append(`prices[${index}]`, price)
          })
        } else {
          formData.append(key, this.form[key])
        }
      })

      this.form.post(`/products/${this.form.id}`, {
        forceFormData: true
      })
    },
    destroy() {
      if (confirm('¿Estás seguro que quieres eliminar este producto?')) {
        this.$inertia.delete(`/products/${this.product.id}`)
      }
    },
  },
}
</script>
